import React from "react";
import { Link } from "react-router-dom";
import { useSEO } from "@/lib/useSEO";
import LegalPageLayout, { LegalSection } from "@/components/LegalPageLayout";

export default function PrivacyPolicyPage() {
  useSEO({
    title: "Privacy Policy — KARM",
    description: "How KARM collects, uses, and protects your data.",
    path: "/privacy",
  });

  return (
    <LegalPageLayout eyebrow="Legal" title="Privacy Policy" updated="22 July 2026">
      <LegalSection heading="1. Overview">
        <p>
          KARM ("we", "us") operates a job search platform focused on roles at Global Capability
          Centres (GCCs) worldwide. This policy explains what
          information we collect when you use karm.day, how we use it, and the choices you have.
        </p>
      </LegalSection>

      <LegalSection heading="2. Information We Collect">
        <p>
          <strong className="text-foreground">Account data</strong> — name, email, and password
          (hashed) if you sign up directly, or your name/email/profile picture if you sign in with
          Google.
        </p>
        <p>
          <strong className="text-foreground">Profile &amp; resume data</strong> — any resume
          files, skills, or career details you choose to upload or enter, used solely to show you
          relevant roles.
        </p>
        <p>
          <strong className="text-foreground">Usage data</strong> — pages viewed, jobs saved or
          applied to, and basic device/browser information, used to keep the product working and
          to improve it.
        </p>
      </LegalSection>

      <LegalSection heading="3. How We Use Your Information">
        <p>
          To operate your account, show you job listings and GCC profiles, send you optional
          email updates (e.g. weekly digests) you can unsubscribe from at any time, and to keep
          the platform secure and reliable. We do not sell your personal data.
        </p>
      </LegalSection>

      <LegalSection heading="4. Cookies &amp; Local Storage">
        <p>
          We use minimal cookies/local storage to keep you signed in and remember preferences
          like light/dark theme. We do not use third-party advertising trackers to sell your data.
        </p>
      </LegalSection>

      <LegalSection heading="5. Third-Party Services">
        <p>
          We rely on a small number of trusted providers to run KARM: Google (sign-in), Resend
          (transactional email/OTP delivery), and a payment processor for optional paid features.
          Each only receives the minimum data needed to perform its function.
        </p>
      </LegalSection>

      <LegalSection heading="6. Data Retention &amp; Security">
        <p>
          We retain account and resume data for as long as your account is active, and delete it
          on request. We use industry-standard measures (encrypted connections, hashed passwords)
          to protect your data, though no system can be guaranteed 100% secure.
        </p>
      </LegalSection>

      <LegalSection heading="7. Your Rights">
        <p>
          You can access, update, or delete your profile and resumes at any time from{" "}
          <Link to="/settings" className="underline text-foreground">Settings</Link>, or write to
          us to request full account deletion.
        </p>
      </LegalSection>

      <LegalSection heading="8. Children's Privacy">
        <p>KARM is intended for working professionals and job seekers aged 18 and above.</p>
      </LegalSection>

      <LegalSection heading="9. Changes to This Policy">
        <p>
          We may update this policy occasionally. Material changes will be reflected by updating
          the "Last updated" date above.
        </p>
      </LegalSection>

      <LegalSection heading="10. Contact Us">
        <p>
          Questions about this policy? Reach us at{" "}
          <a href="mailto:hello@karm.day" className="underline text-foreground">hello@karm.day</a>{" "}
          or visit our <Link to="/contact" className="underline text-foreground">Contact page</Link>.
        </p>
      </LegalSection>
    </LegalPageLayout>
  );
}
